import { lazy, Suspense, useEffect, useEffectEvent, useRef, useState, type ReactNode } from "react";
import { Lock, RefreshCw } from "lucide-react";
import { CardSetupLayout } from "@/features/payment-methods/components/CardSetupLayout";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { paymentApiService as api } from "../services/paymentApiService";
import {
  canContinueCustomerPayment, formatPaymentMoney, fromPaymentDetailsDtoToPaymentDetails, fromPaymentDtoToPayment,
  fromPaymentPreparationDtoToPaymentPreparation, isPaymentTerminal,
  needsPaymentAuthentication,
  type Payment, type PaymentDetails, type PaymentPreparation,
} from "../types/paymentTypes";
import { PaymentState } from "./PaymentState";

const StripePayment = lazy(() => import("./StripePayment"));
const STATUS_POLL_MS = 3000;
const MAX_STATUS_POLLS = 10;

export function CustomerPaymentFlow({ token, onDone, embedded = false, onStatusChange }: {
  token: string; onDone?: () => void; embedded?: boolean; onStatusChange?: (payment: Payment) => void;
}) {
  const [details, setDetails] = useState<PaymentDetails>();
  const [payment, setPayment] = useState<Payment>();
  const [preparation, setPreparation] = useState<PaymentPreparation>();
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [confirmationAttempted, setConfirmationAttempted] = useState(false);
  const controller = useRef<AbortController | null>(null);
  const submitting = useRef(false);
  const checkingRef = useRef(false);
  const [polls, setPolls] = useState(0);
  const startEmbedded = useEffectEvent((info: PaymentDetails, currentPayment: Payment, signal: AbortSignal) =>
    prepare(info, currentPayment, signal));
  // Bank verification and processing settle shortly after confirmation; check a bounded number of times.
  const awaitingResult = !!payment && !isPaymentTerminal(payment) && (confirmationAttempted || payment.status === "processing");
  const polling = awaitingResult && polls < MAX_STATUS_POLLS && !error;
  const poll = useEffectEvent(() => { setPolls(count => count + 1); void checkStatus(); });
  useEffect(() => {
    if (!polling || checking) return;
    const timer = window.setTimeout(poll, STATUS_POLL_MS);
    return () => window.clearTimeout(timer);
  }, [polling, polls, checking]);
  useEffect(() => {
    const current = new AbortController();
    controller.current = current;
    api.open(token, current.signal).then(fromPaymentDetailsDtoToPaymentDetails).then(async result => {
      if (!current.signal.aborted) {
        setDetails(result); setPayment(result.payment); onStatusChange?.(result.payment);
        if (embedded && canContinueCustomerPayment(result.payment)) await startEmbedded(result, result.payment, current.signal);
      }
    }).catch(() => {
      if (!current.signal.aborted) setError("This payment link is unavailable or expired. Try again, or ask the business for a new link.");
    });
    return () => current.abort();
  }, [token, attempt, embedded, onStatusChange]);

  async function checkStatus() {
    const signal = controller.current?.signal;
    if (!signal || signal.aborted || !details || checkingRef.current) return;
    checkingRef.current = true;
    setChecking(true);
    setError("");
    try {
      const result = fromPaymentDtoToPayment(await api.status(token, signal));
      if (result.id !== details.payment.id || result.amountMinor !== details.amountMinor || result.currency !== details.currency) {
        throw new Error("The payment context changed.");
      }
      if (!signal.aborted) { setPayment(result); onStatusChange?.(result); }
    } catch {
      if (!signal.aborted) setError("Couldn't verify payment status. It may still have completed. Ask the business to check this payment; do not pay again.");
    } finally {
      checkingRef.current = false;
      if (!signal.aborted) setChecking(false);
    }
  }
  async function prepare(info = details, currentPayment = payment, signal = controller.current?.signal) {
    if (!signal || signal.aborted || !info || !currentPayment || submitting.current || !canContinueCustomerPayment(currentPayment)) return;
    submitting.current = true;
    setBusy(true);
    setError("");
    try {
      const result = fromPaymentPreparationDtoToPaymentPreparation(await api.prepare(token, signal));
      if (result.publishableKey !== info.publishableKey || result.stripeAccountId !== info.stripeAccountId) {
        throw new Error("Stripe payment context changed.");
      }
      if (!signal.aborted) { setPreparation(result); setConfirmationAttempted(false); }
    } catch {
      if (!signal.aborted) setError("Couldn't open the secure payment form. Check this payment's status before trying again.");
    } finally {
      submitting.current = false;
      if (!signal.aborted) setBusy(false);
    }
  }
  const shell = (content: ReactNode) => embedded ? content : <CardSetupLayout details={details} purpose="payment"
    description={payment && !isPaymentTerminal(payment) && payment.method === "new_card" ?
      "Your card won't be saved for future payments. Your bank may ask you to approve this payment." : undefined}
    title={payment?.status === "succeeded" ? "Payment complete" :
      payment?.status === "canceled" ? "Payment canceled" :
        payment?.status === "voided" ? "Payment unavailable" :
          payment && needsPaymentAuthentication(payment) ? "Verify your payment" : undefined}>{content}</CardSetupLayout>;
  if (!details || !payment) return shell(error ? <ErrorState message={error}
    onRetry={() => { setError(""); setAttempt(value => value + 1); }} /> : <LoadingSpinner message="Loading payment..." />);
  const canPrepare = canContinueCustomerPayment(payment);
  const authentication = needsPaymentAuthentication(payment);
  const amountLabel = formatPaymentMoney(details.amountMinor, details.currency);
  return shell(<div className="space-y-4">
    {!embedded && details.environment === "test" && <p className="text-xs text-muted-foreground">Test mode - use test cards only.</p>}
    {!embedded && <PaymentState payment={payment} audience="customer" />}
    {!isPaymentTerminal(payment) && <>
      {embedded && busy && <LoadingSpinner message="Preparing secure payment..." />}
      {canPrepare && (!preparation || confirmationAttempted) && !error && (!embedded || confirmationAttempted || !busy) && <Button
        className={embedded ? undefined : "h-12 w-full"} disabled={busy || checking}
        onClick={() => void prepare()}>{busy ? "Preparing payment..." : authentication ? "Continue to bank verification" :
          confirmationAttempted ? "Reopen payment form" : embedded ? "Continue to secure payment" : "Continue"}</Button>}
      {preparation && !confirmationAttempted && canPrepare && <Suspense fallback={<LoadingSpinner message="Loading secure payment..." />}>
        <StripePayment key={preparation.clientSecret} preparation={preparation} amountLabel={amountLabel}
          returnUrl={`${window.location.origin}/payments/pay#token=${encodeURIComponent(token)}`}
          onConfirmed={async () => { setConfirmationAttempted(true); setPreparation(undefined); setPolls(0); await checkStatus(); }} />
      </Suspense>}
    </>}
    {error && <ErrorState message={error} />}
    {!isPaymentTerminal(payment) && (!!error || awaitingResult && !polling) && <Button variant="quiet"
      className={embedded ? undefined : "w-full"} disabled={busy || checking} onClick={() => { setPolls(0); void checkStatus(); }}>
      <RefreshCw aria-hidden="true" className={checking ? "animate-spin" : undefined} />
      Retry payment status
    </Button>}
    {isPaymentTerminal(payment) && (onDone ? <Button className={embedded ? undefined : "h-12 w-full"} onClick={onDone}>Done</Button> :
      <p className="text-center text-sm text-muted-foreground">You can close this page.</p>)}
    {!embedded && !isPaymentTerminal(payment) && <p className="flex flex-wrap items-center justify-center gap-1.5 text-center text-xs text-muted-foreground">
      <Lock aria-hidden="true" className="size-3.5" />
      Secured by Stripe · Link expires {details.expiresAt.toLocaleString()}
    </p>}
  </div>);
}
